import React, { useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import "../../styles/ExecutionLogsModal.css";
import { WorkflowExecution } from "../../../workflow/types/workflow.types";

interface ExecutionStep {
  id: string;
  node_name: string;
  node_key: string;
  step_order: number;
  status: string;
  started_at: string;
  finished_at: string;
  duration_ms: number;
  error_message?: string;
  input_data?: any;
  output_data?: any;
}

interface ExecutionLog {
  id: string;
  step_id: string | null;
  log_level: string;
  message: string;
  data?: any;
  created_at: string;
}

interface ExecutionData {
  id: string;
  execution_number: number;
  status: string;
  trigger_data: any;
  started_at: string;
  finished_at: string;
  duration_seconds: number;
  error?: string;
  steps: ExecutionStep[];
  logs: ExecutionLog[];
}

interface Props {
  visible: boolean;
  onClose: () => void;
  execution: ExecutionData | null;
  details?: WorkflowExecution | null;
  loadingDetails?: boolean;
}

const MAX_JSON_CHARS = 60000;

const renderJson = (data: any) => {
  let json = "";
  try {
    json = JSON.stringify(data, null, 2);
  } catch {
    return "[unserializable payload]";
  }
  if (json.length > MAX_JSON_CHARS) {
    return `${json.slice(0, MAX_JSON_CHARS)}\n… [truncated ${json.length - MAX_JSON_CHARS
      } chars — large payloads are capped for display]`;
  }
  return json;
};

const wasTruncated = (data: any) => {
  try {
    return JSON.stringify(data).includes("__truncated");
  } catch {
    return false;
  }
};

export const ExecutionLogsModal: React.FC<Props> = ({
  visible,
  onClose,
  execution,
  details,
  loadingDetails = false,
}) => {
  useEffect(() => {
    if (visible) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = "unset";
      document.body.style.paddingRight = "0px";
    };
  }, [visible]);

  const logsByStep = useMemo(() => {
    if (!execution) return { workflow: [] };

    const grouped: Record<string, ExecutionLog[]> = {
      workflow: [],
    };

    execution.logs.forEach((log) => {
      const key = log.step_id || "workflow";
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(log);
    });

    return grouped;
  }, [execution]);

  const stats = useMemo(() => {
    if (!execution) {
      return {
        totalSteps: 0,
        completedSteps: 0,
        failedSteps: 0,
        totalLogs: 0,
        errorLogs: 0,
        warningLogs: 0,
      };
    }

    return {
      totalSteps: execution.steps.length,
      completedSteps: execution.steps.filter(
        (s) => s.status.toLowerCase() === "completed"
      ).length,
      failedSteps: execution.steps.filter(
        (s) => s.status.toLowerCase() === "failed"
      ).length,
      totalLogs: execution.logs.length,
      errorLogs: execution.logs.filter(
        (l) => l.log_level.toLowerCase() === "error"
      ).length,
      warningLogs: execution.logs.filter(
        (l) => l.log_level.toLowerCase() === "warning"
      ).length,
    };
  }, [execution]);

  const detailMatches =
    !!details && String(details.id) === String(execution?.id);

  const detailStepsById = useMemo(() => {
    const map: Record<string, any> = {};
    if (detailMatches && details && Array.isArray(details.steps)) {
      details.steps.forEach((s: any) => {
        map[String(s?.id)] = s;
      });
    }
    return map;
  }, [detailMatches, details]);

  if (!visible || !execution) return null;

  const {
    execution_number,
    status,
    steps,
    logs,
    started_at,
    finished_at,
    duration_seconds,
  } = execution;

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M9 12L11 14L15 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        );
      case "failed":
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M15 9L9 15M9 9L15 15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        );
      case "running":
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M12 6V12L16 14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const getLogLevelIcon = (level: string) => {
    switch (level.toLowerCase()) {
      case "info":
        return "ℹ️";
      case "error":
        return "❌";
      case "warning":
        return "⚠️";
      case "success":
        return "✅";
      default:
        return "📝";
    }
  };

  const getLogLevelClass = (level: string) => {
    return `log-level-${level.toLowerCase()}`;
  };

  const renderDataBlock = (label: string, data: any) => {
    if (data === null || data === undefined) return null;
    return (
      <div className="data-block">
        <div className="data-label">{label}</div>
        {wasTruncated(data) && (
          <div className="data-truncated-note">
            ⚠️ Large payload — arrays are capped to the first 100 items for
            display. View the full list on the dashboard.
          </div>
        )}
        <pre className="data-json">{renderJson(data)}</pre>
      </div>
    );
  };

  return createPortal(
    <div className="execution-modal-overlay" onClick={onClose}>
      <div className="execution-modal" onClick={(e) => e.stopPropagation()}>
        <div className={`execution-header ${status.toLowerCase()}`}>
          <div className="execution-status">
            <div className={`status-badge ${status.toLowerCase()}`}>
              {getStatusIcon(status)}
            </div>
            <div className="status-text">
              <h2>Execution #{execution_number}</h2>
              <p>
                {steps.length} step{steps.length !== 1 ? "s" : ""} •{" "}
                {logs.length} log{logs.length !== 1 ? "s" : ""} •{" "}
                {duration_seconds}s
              </p>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="execution-stats">
          <div className="stat-item">
            <div className="stat-value">{stats.totalSteps}</div>
            <div className="stat-label">Total Steps</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item success">
            <div className="stat-value">{stats.completedSteps}</div>
            <div className="stat-label">Completed</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item error">
            <div className="stat-value">{stats.failedSteps}</div>
            <div className="stat-label">Failed</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-value">{stats.totalLogs}</div>
            <div className="stat-label">Logs</div>
          </div>
        </div>

        <div className="execution-body">
          {logsByStep.workflow && logsByStep.workflow.length > 0 && (
            <div className="logs-section">
              <div className="section-header workflow">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <h3>Workflow Events</h3>
                <span className="log-count">{logsByStep.workflow.length}</span>
              </div>
              <div className="logs-list">
                {logsByStep.workflow.map((log) => (
                  <div
                    key={log.id}
                    className={`log-entry ${getLogLevelClass(log.log_level)}`}>
                    <div className="log-icon">
                      {getLogLevelIcon(log.log_level)}
                    </div>
                    <div className="log-content">
                      <div className="log-message">{log.message}</div>
                      <div className="log-meta">
                        <span className="log-time">
                          {moment(log.created_at).format("HH:mm:ss.SSS")}
                        </span>
                        {log.data && Object.keys(log.data).length > 0 && (
                          <span
                            className="log-data-badge"
                            title={JSON.stringify(log.data, null, 2)}>
                            {Object.keys(log.data).length} data field
                            {Object.keys(log.data).length > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {steps.map((step) => {
            const stepLogs = logsByStep[step.id] || [];
            return (
              <div key={step.id} className="step-section">
                <div className={`step-header ${step.status.toLowerCase()}`}>
                  <div className="step-info">
                    <div className={`step-badge ${step.status.toLowerCase()}`}>
                      {getStatusIcon(step.status)}
                    </div>
                    <div className="step-details">
                      <div className="step-title">
                        <span className="step-number">
                          Step {step.step_order}
                        </span>
                        <span className="step-name">{step.node_name}</span>
                      </div>
                      <div className="step-meta">
                        <span className="step-key">{step.node_key}</span>
                        <span className="step-duration">
                          {step.duration_ms}ms
                        </span>
                        <span className="step-time">
                          {moment(step.started_at).format("HH:mm:ss")} →{" "}
                          {moment(step.finished_at).format("HH:mm:ss")}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="step-status-label">{step.status}</div>
                </div>

                {step.error_message && (
                  <div className="step-error">
                    <div className="error-icon">❌</div>
                    <div className="error-text">{step.error_message}</div>
                  </div>
                )}

                {stepLogs.length > 0 && (
                  <div className="step-logs">
                    {stepLogs.map((log) => (
                      <div
                        key={log.id}
                        className={`log-entry ${getLogLevelClass(
                          log.log_level
                        )}`}>
                        <div className="log-icon">
                          {getLogLevelIcon(log.log_level)}
                        </div>
                        <div className="log-content">
                          <div className="log-message">{log.message}</div>
                          <div className="log-meta">
                            <span className="log-time">
                              {moment(log.created_at).format("HH:mm:ss.SSS")}
                            </span>
                            {log.data && Object.keys(log.data).length > 0 && (
                              <span
                                className="log-data-badge"
                                title={JSON.stringify(log.data, null, 2)}>
                                {Object.keys(log.data).length} data field
                                {Object.keys(log.data).length > 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <details className="data-section">
                  <summary>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 18l6-6-6-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    View Input/Output Data
                  </summary>
                  <div className="data-content">
                    {(() => {
                      const detailStep = detailStepsById[String(step.id)];
                      if (loadingDetails && !detailStep) {
                        return (
                          <div className="data-loading">Loading data…</div>
                        );
                      }
                      if (!detailStep) {
                        return (
                          <div className="data-empty">
                            No data available for this step.
                          </div>
                        );
                      }
                      if (!detailStep.input_data && !detailStep.output_data) {
                        return (
                          <div className="data-empty">
                            No input/output recorded.
                          </div>
                        );
                      }
                      return (
                        <>
                          {renderDataBlock("Input Data", detailStep.input_data)}
                          {renderDataBlock(
                            "Output Data",
                            detailStep.output_data
                          )}
                        </>
                      );
                    })()}
                  </div>
                </details>
              </div>
            );
          })}
        </div>

        <div className="execution-footer">
          <div className="footer-info">
            <span>
              Started: {moment(started_at).format("MMM DD, YYYY HH:mm:ss")}
            </span>
            <span>•</span>
            <span>
              Finished: {moment(finished_at).format("MMM DD, YYYY HH:mm:ss")}
            </span>
          </div>
          <button className="footer-btn primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
